import {
  Component,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { PictureDisplay } from '@freelancer/ui/picture';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';

export enum FileDisplayType {
  ICON = 'icon',
  THUMBNAIL = 'thumbnail',
}

export enum FileDisplaySize {
  XSMALL = 'xsmall',
  SMALL = 'small',
  MID = 'mid',
}

@Component({
  selector: 'fl-file-display',
  template: `
    <ng-container *ngIf="type === FileDisplayType.THUMBNAIL">
      <img
        class="FileDisplay Image"
        *ngIf="isImage"
        [src]="src"
        [alt]="alt"
        [attr.data-size]="size"
      />

      <fl-icon
        class="FileDisplay Icon"
        *ngIf="!isImage"
        [ngClass]="{ IsLarge: size !== FileDisplaySize.XSMALL }"
        [size]="IconSize.MID"
        [color]="IconColor.WHITE"
        [name]="iconName"
      ></fl-icon>
    </ng-container>

    <fl-icon
      *ngIf="type === FileDisplayType.ICON"
      [color]="iconColor"
      [name]="iconName"
      [size]="iconSize"
    ></fl-icon>
  `,
  styleUrls: ['./file-display.component.scss'],
})
export class FileDisplayComponent implements OnChanges {
  FileDisplaySize = FileDisplaySize;
  FileDisplayType = FileDisplayType;
  IconColor = IconColor;
  IconSize = IconSize;
  PictureDisplay = PictureDisplay;

  @Input() alt: string;
  @Input() src: string;
  @Input() iconColor = IconColor.DARK;
  @Input() iconSize = IconSize.MID;

  @HostBinding('attr.data-type')
  @Input()
  type = FileDisplayType.THUMBNAIL;

  // Thumbnail size
  @HostBinding('attr.data-size')
  @Input()
  size = FileDisplaySize.SMALL;

  iconName: string;
  isImage: boolean;

  constructor(@Inject(UI_CONFIG) public uiConfig: UiConfig) {}

  ngOnChanges(changes: SimpleChanges) {
    if ('src' in changes) {
      const fileparts = this.src.split('.');
      const fileType =
        fileparts.length > 1
          ? fileparts[fileparts.length - 1].toLowerCase()
          : '';

      this.isImage = this.isImageFile(fileType);
      this.iconName = this.getFileIcon(fileType);
    }
  }

  isImageFile(fileType: string): boolean {
    // these are the image files that can be viewed on all browsers we support
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(fileType);
  }

  getFileIcon(fileType: string): string {
    switch (fileType) {
      case 'doc':
      case 'docx':
        return 'ui-file-doc';
      case 'gif':
        return 'ui-file-gif';
      case 'jpg':
      case 'jpeg':
        return 'ui-file-jpg';
      case 'key':
        return 'ui-file-key';
      case 'mov':
        return 'ui-file-mov';
      case 'mpg':
        return 'ui-file-mpg';
      case 'pdf':
        return 'ui-file-pdf';
      case 'png':
        return 'ui-file-png';
      case 'pps':
      case 'ppt':
      case 'pptx':
        return 'ui-file-pps';
      case 'rar':
        return 'ui-file-rar';
      case 'rtf':
        return 'ui-file-rtf';
      case 'svg':
        return 'ui-file-svg';
      case 'txt':
        return 'ui-file-txt';
      case 'xls':
      case 'xlsx':
        return 'ui-file-xls';
      case 'zip':
        return 'ui-file-zip';
      default:
        return 'ui-file';
    }
  }
}
